/**
 * What's on the platform, in one place.
 *
 * Every coach-facing surface gets one entry here, and everything that has to
 * explain the platform reads from this file rather than carrying its own copy:
 * the tour page, the empty states, the dashboard's "you haven't opened this"
 * card. The welcome email deliberately does NOT list features — it links to
 * the tour, so it can never go stale.
 *
 * The reason for a registry rather than a written page: a written page is
 * accurate on the day it's written. This one is checked. platformMap.test.js
 * walks NAV and fails the build if a sidebar item has no entry here, or has
 * one with nothing in `when`. Ship a feature without describing it and the
 * tests go red — which is the only version of "keep the tour updated" that
 * survives contact with a busy month.
 *
 * Four fields, and they are not interchangeable:
 *
 *   what   one line. What the thing is.
 *   doing  what a coach actually does there, in verbs.
 *   when   the moment they'd reach for it. This is the field that makes the
 *          difference between a tour and a directory, and it's the one the
 *          test insists on, because it's the one that's tempting to skip.
 *   route  the hash the sidebar uses, so every entry is one tap from itself.
 *
 * `status` is honest rather than flattering. "soon" means there is nothing
 * behind the door yet and we say so; "training" means it answers but is still
 * learning and can be wrong. A tour that oversells is worse than no tour: a
 * coach who opens three doors onto empty rooms stops opening doors.
 *
 * `added` is the ship date, and it earns its keep twice — a "new since you
 * joined" marker for coaches who were already here, and the source for a
 * what-shipped roundup that never needs writing by hand.
 */

// The question a coach is actually asking, which is not the same shape as the
// sidebar. Sidebar order is for people who already know where things are.
import { FEATURES } from "../utils/featureFlags";

export const TOUR_GROUPS = [
  { id: "practice",  title: "I need something for practice tomorrow",
    blurb: "The library, and the tools that turn it into a session." },
  { id: "craft",     title: "I want to get better at coaching",
    blurb: "The long game: ideas, vocabulary, and the certification." },
  { id: "stuck",     title: "I have a specific problem",
    blurb: "Ways to find an answer, or a person who has one." },
  { id: "own",       title: "I want to work out what I actually believe",
    blurb: "Your own thinking, written down and kept." },
  { id: "tools",     title: "Tools",
    blurb: "Bigger pieces of software that live inside the platform." },
  { id: "keeping",   title: "Housekeeping",
    blurb: "The app, your account, and what reaches you." },
];

export const PLATFORM_MAP = [
  /* ── I need something for practice tomorrow ───────────────────────────── */
  {
    id: "pd", visitKey: "pd", group: "practice", name: "Player Development drills & games",
    route: "#/pd", status: "live", added: "2025-01-01",
    what: "The individual-skills half of the library: shooting, finishing, ballhandling, attacking, passing.",
    doing: "Filter by skill, by level, and by whether a drill is on-air or a small-sided game. Watch it, bookmark it, write your own version onto it.",
    when: "You know the skill you want to work and you want options you haven't run into the ground.",
  },
  {
    id: "team", visitKey: "team", group: "practice", name: "Team Coaching drills & games",
    route: "#/team", status: "live", added: "2025-01-01",
    what: "The team half: snapshot games, transition, defense, half-court offense, spacing, scrimmage formats.",
    doing: "Same filters, built around what you're trying to teach a group rather than a player.",
    when: "You're building a practice and you need the middle 40 minutes.",
  },
  {
    // Not a sidebar row: the way in is the Pro Development chip on Drills &
    // Games. It is still a real route, so it gets a tour entry and a
    // link you can send someone.
    id: "pro", visitKey: "pro", group: "practice", name: "Pro Development",
    route: "#/pro", status: "live", added: "2026-09-19",
    what: "A curated shelf of full workouts and individual drills filmed in our sessions with pro players.",
    doing: "Open it from the Pro Development chip on Drills & Games. Filter by whether you want a whole workout, a single drill, or the insight videos that came out of the same sessions.",
    when: "You want to see how a session actually runs at the top level, not just the drill in isolation.",
  },
  {
    // Not a sidebar row: the way in is the research block on Resources, the
    // Learning Lab's research cards and the Drop. The one Research home:
    // collections first, then every paper, searchable by the problem.
    id: "research", visitKey: "research", group: "craft", name: "Research",
    route: "#/research", status: "live", added: "2026-09-26",
    what: "Every study on the platform in one place: collections that make one argument, and every paper with what it found up front.",
    doing: "Read a collection straight through, or search by the problem in your gym, filter by category, open a paper for the reviewed read, and save the ones to come back to.",
    when: "You want to know what the evidence actually says about something, and you do not want to assemble it yourself from a list of papers.",
  },
  {
    // Not a sidebar row: Home leads with it for the week after it lands,
    // and the email and push open it. Draft copy, for Coleman to edit.
    id: "drop", visitKey: "drop", group: "practice", name: "The Wednesday Drop",
    route: "#/drop", status: "live", added: "2026-10-15",
    what: "One theme a week, with what's new built around it: drills, X&Os, research, a science module, and a package of things already here.",
    doing: "Open this week's drop, take what fits your next practice, and post what happened in its Community thread.",
    when: "It's midweek and you want one thing to work on with your players before the weekend, already picked out.",
  },
  {
    id: "drops", visitKey: "drops", group: "practice", name: "Past drops",
    route: "#/drops", status: "live", added: "2026-10-15",
    what: "Every Wednesday Drop so far, newest first, each one still whole.",
    doing: "Scroll back by theme and open any week's drop the way it went out.",
    when: "You remember a week that had the thing you need now, or you joined late and want the earlier themes.",
  },
  {
    id: "movement", visitKey: "movement", group: "practice", name: "Movement Drills",
    route: "#/movement", status: "live", added: "2025-01-01",
    what: "Warmups, plyometrics, coordination, mobility, physicality.",
    doing: "Pull a warmup that isn't the same three lines, or build a movement block into a session.",
    when: "The first fifteen minutes, or any time an athlete needs the physical piece and not more reps.",
  },
  {
    id: "workouts", visitKey: "workouts", group: "practice", name: "Workout Examples",
    route: "#/workouts", status: "live", added: "2025-01-01",
    what: "Complete on-court workouts, individual and group, start to finish.",
    doing: "Run one as it stands, or take its shape and swap the drills for yours.",
    when: "You have an hour with a player and you don't want to invent it in the car.",
  },
  {
    id: "plans", visitKey: "plans", group: "practice", name: "Training Plans",
    route: "#/plans", status: "live", added: "2025-01-01",
    what: "Ready-made practice plans by length, level, and setting, plus the builder for your own.",
    doing: "Open a plan, edit it, or start from nothing. Drills you've bookmarked show up here so you're not searching twice.",
    when: "The night before practice.",
  },
  {
    id: "playbook", visitKey: "playbook", group: "practice", name: "Playbook",
    route: "#/playbook", status: "live", added: "2026-09-11",
    what: "A set or action a day with the coaching point, and the playbook you build from the ones you'd run.",
    doing: "Watch today's set, save it with your own note, and draw a play yourself for anything we don't have.",
    when: "You want one new look a day, or you need your system on paper for the staff or the team.",
  },

  {
    id: "calls", visitKey: "calls", group: "practice", name: "Calls",
    route: "#/calls", status: "live", added: "2026-09-29",
    what: "The live group calls: when the next one is, the standing weekly slot, and the link to join.",
    doing: "Join the call, or put the whole schedule in your own calendar so you stop having to check.",
    when: "You want to ask something out loud rather than type it, or you half remember there's a call tonight.",
  },

  /* ── I want to get better at coaching ─────────────────────────────────── */
  {
    id: "master", visitKey: "master", group: "craft", name: "Masterclasses",
    route: "#/master", status: "live", added: "2025-01-01",
    what: "Long-form video series from coaches worth the hours.",
    doing: "Work through a series, one session at a time, and keep your place between sittings.",
    when: "You've got a drive, a flight, or an off-season.",
  },
  {
    id: "insights", visitKey: "insights", group: "craft", name: "Player Development Insights",
    route: "#/insights", status: "live", added: "2025-01-01",
    what: "Written reads on development philosophy: methodology, psychology, culture, research.",
    doing: "Read one, argue with it, take the part that fits your gym.",
    when: "You know what you do but not yet why, or you know why and want to check it.",
  },
  {
    id: "ti", visitKey: "ti", group: "craft", name: "Team Insights",
    route: "#/ti", status: "live", added: "2025-01-01",
    what: "The same, one level up: system design, team culture, communication, analytics.",
    doing: "Read frameworks for the decisions that shape a season rather than a session.",
    when: "You're planning a year, not a Tuesday.",
  },
  {
    id: "xo", visitKey: "xo", group: "craft", name: "X & O Breakdowns",
    route: "#/xo", status: "live", added: "2025-01-01",
    what: "Sets, actions and concepts broken down from pro and international basketball.",
    doing: "Study sets, ATOs, baseline and sideline out-of-bounds, half-court actions, and steal what fits your personnel.",
    when: "You need a look for a specific situation, or you saw something on TV and want to know how it works.",
  },
  {
    id: "foundations", visitKey: "foundations", group: "craft", name: "Foundations",
    route: "#/foundations", status: "live", added: "2026-09-01",
    what: "The vocabulary underneath everything else: one concept per card, in plain language.",
    doing: "Read a concept, flip through the cards behind it, and ask the community when a definition doesn't sit right.",
    when: "A word keeps coming up and you've been nodding along. New batches land regularly.",
  },
  {
    id: "learn", visitKey: "learn", group: "craft", name: "Learning Lab",
    route: "#/learn", status: "live", added: "2026-09-02",
    what: "A short daily session in three beats: watch one thing, work a lane, answer one challenge.",
    doing: "Pick a lane or take the one it suggests, and write on what you see. What you write is kept on the drill.",
    when: "Fifteen minutes with a coffee. It's the one thing here designed to be done every day rather than when you need it.",
  },
  {
    id: "certification", visitKey: "certified", group: "craft", name: "BAM Certification",
    route: "#/certified", status: "live", added: "2025-01-01",
    what: "Nine units and a final exam, on the method the rest of the platform is built on.",
    doing: "Work the units in order, take the quizzes, sit the exam, get certified.",
    when: "You want the whole framework rather than the pieces, in the order it was meant to be learned.",
  },

  /* ── I have a specific problem ────────────────────────────────────────── */
  {
    id: "community", visitKey: "community", group: "stuck", name: "Community",
    route: "#/community", status: "live", added: "2025-01-01",
    what: "The feed. Coaches asking coaches.",
    doing: "Post a question, answer someone else's, and attach the drill or the film clip you're talking about so nobody has to guess.",
    when: "The answer is in someone's experience rather than in a library.",
  },
  {
    id: "chats", visitKey: "chats", group: "stuck", name: "Chats",
    route: "#/chats", status: "live", added: "2025-01-01",
    what: "Direct and group messages with coaches you've met here.",
    doing: "Talk without doing it in front of everybody. Mute a thread or set it to mentions only.",
    when: "The conversation is one-to-one, or the group is small enough to have names.",
  },
  {
    id: "search", group: "stuck", name: "Search and the command palette",
    route: "#/dashboard", status: "live", added: "2025-01-01",
    what: "Search finds drills by what they teach. The command palette jumps anywhere in one keystroke.",
    doing: "Search a concept rather than a title. Hit Cmd+K (Ctrl+K on Windows) to go anywhere without the sidebar.",
    when: "You know what you want and you don't want to browse for it. Almost nobody finds the palette on their own, which is why it's here.",
  },
  {
    id: "askbam", group: "stuck", name: "Ask BAM",
    route: "#/dashboard", status: "training", added: "2025-01-01", flag: "askbam",
    what: "A coaching assistant in the bottom-right of every page, drawing on the same method the certification teaches.",
    doing: "Ask it a coaching question in plain language.",
    when: "You want a starting point fast. It's still in training, so treat what it says as a first draft and check it against the library.",
  },
  {
    id: "map", group: "stuck", name: "The coach map",
    route: "#/community", status: "live", added: "2025-06-01", flag: "memberMap",
    what: "Everyone on the platform, on a map.",
    doing: "See who's near you, or who's coaching in a country you're curious about, and message them.",
    when: "You want the person more than the answer.",
  },

  /* ── I want to work out what I actually believe ───────────────────────── */
  {
    id: "portal", visitKey: "portal", group: "own", name: "My Portal",
    route: "#/portal", status: "live", added: "2025-05-01",
    what: "Everything you've made or kept, in one place: plans, films, uploads, bookmarks.",
    doing: "Find the thing you saved three weeks ago without remembering which screen you saved it on.",
    when: "You know you had it somewhere.",
  },
  {
    id: "makeityours", group: "own", name: "Make it yours",
    route: "#/pd", status: "live", added: "2026-09-05",
    what: "Five questions on every drill in the library: harder, easier, remix, cues, when you'd use it.",
    doing: "Write your version of a drill onto the drill. It's there next time, and it's the same object whether you wrote it while browsing or inside a Lab session.",
    when: "Any time you watch something and immediately think \"not with my group\". That thought is worth keeping.",
  },
  {
    id: "notes", group: "own", name: "Notes and bookmarks",
    route: "#/portal", status: "live", added: "2025-01-01",
    what: "A note field on every drill, and a bookmark that follows the drill everywhere.",
    doing: "Bookmark as you browse. Bookmarks surface on your dashboard and inside the plan builder, so saving now saves searching later.",
    when: "While you're watching, not after. Nobody comes back to write the note.",
  },

  {
    id: "iq", visitKey: "iq", group: "stuck", name: "BAM IQ",
    route: "#/iq", status: "beta", added: "2026-10-19", flag: "bamiq",
    what: "An AI coaching partner that thinks through your problem with you and draws on BAM's library when it helps.",
    doing: "Describe what you're seeing, ask for a drill or a tactic, push back on its ideas, and open the BAM items it cites.",
    when: "You have a specific coaching problem and want a thinking partner now, not a search through the library.",
  },

  /* ── Tools ────────────────────────────────────────────────────────────── */
  {
    id: "dev-studio", visitKey: "dev-studio", group: "tools", name: "My Players",
    route: "#/dev-studio", status: "live", added: "2026-09-08",
    what: "A development plan per player: their role, what they win with, what holds them back, and the roadmap.",
    doing: "Build a roster, then a plan per player. Rate them on the attribute tree when you want the deeper read.",
    when: "You're accountable for specific players getting better, and you want it written rather than remembered.",
  },
  {
    id: "film", visitKey: "film", group: "tools", name: "BAM Film Studio",
    route: "#/film", status: "beta", added: "2025-07-01", flag: "filmStudio",
    what: "Upload film, tag moments, and pull clips back out.",
    doing: "Tag what you see as you watch, then find every instance of it later without scrubbing.",
    when: "You have game or practice film and re-watching all of it isn't a plan.",
  },
  {
    id: "afs", visitKey: "afs", group: "tools", name: "Advantage Tracking",
    route: "#/afs", status: "beta", added: "2026-10-20", flag: "afsPilot",
    what: "Your tagged games and seasons as advantage numbers: how often you beat your man, keep it, shoot with it and finish, on both ends.",
    doing: "Tag a game, then read a game or a whole season: where advantages come from, where they die, who creates them, and ask questions about it. Keep your staff's definitions in one place.",
    when: "After a game is tagged and you want to know what it says about your offense and your defense.",
  },
  {
    id: "toolbox", visitKey: "toolbox", group: "tools", name: "Toolbox",
    route: "#/toolbox", status: "live", added: "2025-01-01",
    what: "The shelf the tools sit on.",
    doing: "See everything available, including what's coming.",
    when: "You're not sure a tool exists for what you're doing.",
  },
  {
    id: "consulting", visitKey: "consulting", group: "tools", name: "BAM Consulting",
    route: "#/consulting", status: "beta", added: "2025-08-01",
    what: "Work with us directly, and the dashboard for organisations that do.",
    doing: "See what a club or programme engagement involves.",
    when: "You're asking on behalf of a programme rather than yourself.",
  },
  {
    id: "shotflow", group: "tools", name: "ShotFlow",
    route: "#/toolbox", status: "soon", added: "2025-01-01",
    what: "Shooting-form analysis from video.",
    doing: "Nothing yet. It's on the shelf so you know it's coming, not because it's ready.",
    when: "Not yet.",
  },
  {
    id: "shot-prog", group: "tools", name: "Shooting Programs",
    route: "#/toolbox", status: "soon", added: "2025-01-01",
    what: "Structured shooting programmes with progressions.",
    doing: "Nothing yet.",
    when: "Not yet.",
  },

  /* ── Housekeeping ─────────────────────────────────────────────────────── */
  {
    id: "app", group: "keeping", name: "The iPhone app",
    route: "#/settings", status: "live", added: "2025-06-01",
    what: "The whole platform on your phone.",
    doing: "Carry your plans and the library into the gym, where a laptop is useless.",
    when: "Straight away. It's also the only place notifications can reach you.",
  },
  {
    id: "resources", visitKey: "resources", group: "keeping", name: "Resources",
    route: "#/resources", status: "live", added: "2025-01-01",
    what: "The weekly Q&A, the podcast, the reading list, and everything else we make that isn't in the library.",
    doing: "Listen, read, and find the things that live outside the platform.",
    when: "You've got a drive and you've run out of masterclass.",
  },
  {
    id: "referrals", visitKey: "referrals", group: "keeping", name: "Referrals",
    route: "#/referrals", status: "live", added: "2025-04-01",
    what: "Your link, and what it's earned.",
    doing: "Share it with coaches you'd actually recommend this to.",
    when: "Someone asks you where you found a drill.",
  },
  {
    id: "settings", visitKey: "settings", group: "keeping", name: "Settings",
    route: "#/settings", status: "live", added: "2025-01-01",
    what: "Your account, your subscription, and every notification we're allowed to send.",
    doing: "Turn categories on and off one at a time. Nothing here is all-or-nothing except the master switch.",
    when: "The first week, so what reaches you is what you asked for.",
  },
  {
    id: "submit", group: "keeping", name: "Submit content",
    route: "#/dashboard", status: "live", added: "2025-02-01",
    what: "Send us a drill, a game, or a clip.",
    doing: "Upload it with a note about what it teaches. Good ones go in the library with your name on them.",
    when: "You run something that works and it isn't in here.",
  },
];

// A page a coach either has or hasn't opened. The "you haven't opened this"
// card only ever offers these, because a visit is a fair proxy for having
// found them. The feature-level entries (the command palette, Ask BAM, Make
// it yours) deliberately have no visitKey: we can't tell whether somebody has
// used them, and a card that nags about something you already do every day is
// worse than one that never appears. Those live on the tour page instead.
/* An entry behind a feature flag (`flag`) isn't offered while the flag is
   shut: the tour said Ask BAM and the coach map were there when coaches
   couldn't open either. It appears by itself the day the flag flips. */
export const isOpen = (e, features = FEATURES) => !e.flag || !!features[e.flag];

export const nudgeable = () => PLATFORM_MAP.filter(e => e.visitKey && e.status !== "soon" && isOpen(e));

export const PLATFORM_BY_ID = PLATFORM_MAP.reduce((m, e) => { m[e.id] = e; return m; }, {});

// Sidebar ids that legitimately have no entry of their own, with the reason.
// A bare allowlist rots; one that has to say why gets read when it's wrong.
export const MAP_EXEMPT = {
  dashboard: "The dashboard is where the tour is read from, not a stop on it.",
  org:       "A sidebar tile only for members of a consulting organisation, who are onboarded by us directly.",
  interns:   "The Intern Hub: a sidebar tile only for BAM interns, their location leads and program directors, all added by us.",
  filmdesk:  "Film Desk: a sidebar tile only for BAM admins and remote film interns and contributors, all added by us, not a public surface.",
  vocab:     "The old route into Foundations. Kept so existing links resolve; described as Foundations.",
};

// Entries a coach can actually be sent to and get something. The tour and the
// nudge card both draw from this rather than from PLATFORM_MAP, so nothing
// ever points somebody at an empty room.
export const tourEntries = () => PLATFORM_MAP.filter(e => e.status !== "soon" && isOpen(e));

export const groupedEntries = () =>
  TOUR_GROUPS.map(g => ({ ...g, items: PLATFORM_MAP.filter(e => e.group === g.id) }))
    .filter(g => g.items.length > 0);
